import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useI18n } from '../lib/i18n';
import { TIDS } from '../constants/testIds';
import { fetchMemberships } from '../lib/api';

const Memberships = ({ onChoose }) => {
  const { t, lang } = useI18n();
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    fetchMemberships()
      .then((m) => active && setPlans(m))
      .catch((e) => active && setError(e?.message || 'error'));
    return () => {
      active = false;
    };
  }, []);

  const highlightedId = 'plus';

  return (
    <section
      id="memberships"
      data-testid={TIDS.membershipsSection}
      className="py-24 sm:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1D1D1F] leading-tight">
            {t.plans.title}
          </h2>
          <p className="mt-5 text-lg sm:text-xl text-[#86868B] font-medium leading-relaxed">
            {t.plans.subtitle}
          </p>
        </motion.div>

        {error && (
          <p className="mt-8 text-sm text-red-600">Error: {error}</p>
        )}

        <div className="mt-14 sm:mt-20 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {plans.map((plan, i) => {
            const isFeatured = plan.id === highlightedId;
            const features = lang === 'es' ? plan.features_es : plan.features_en;
            const freq = lang === 'es' ? plan.frequency_es : plan.frequency_en;
            return (
              <motion.div
                key={plan.id}
                data-testid={TIDS.membershipCard(plan.id)}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className={`relative flex flex-col rounded-[2rem] p-8 sm:p-10 ${
                  isFeatured
                    ? 'bg-white border-2 border-[#0071E3] shadow-[0_18px_60px_rgba(0,113,227,0.14)] lg:scale-[1.03]'
                    : 'bg-[#F5F5F7]'
                }`}
              >
                {isFeatured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0071E3] text-white text-[11px] font-semibold tracking-wider uppercase px-3 py-1 rounded-full shadow-sm">
                    {t.plans.mostPopular}
                  </span>
                )}

                <div>
                  <p className="text-sm font-semibold tracking-wider uppercase text-[#0071E3]">
                    {plan.name}
                  </p>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="text-2xl font-medium text-[#86868B]">$</span>
                    <span className="text-6xl font-extrabold tracking-tighter text-[#1D1D1F]">
                      {plan.price}
                    </span>
                    <span className="text-base font-medium text-[#86868B] ml-1">
                      {freq}
                    </span>
                  </div>
                </div>

                <ul className="mt-8 space-y-3 flex-1">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex w-5 h-5 rounded-full bg-[#0071E3]/10 items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-[#0071E3]" strokeWidth={3} />
                      </span>
                      <span className="text-[15px] text-[#1D1D1F]/85 leading-relaxed">
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={TIDS.membershipSelect(plan.id)}
                  onClick={() => onChoose?.(plan.id)}
                  className={`mt-8 w-full rounded-full px-6 py-3.5 font-semibold text-[15px] transition-transform hover:scale-[1.02] active:scale-95 ${
                    isFeatured
                      ? 'bg-[#0071E3] hover:bg-[#0077ED] text-white shadow-sm'
                      : 'bg-[#1D1D1F] hover:bg-black text-white'
                  }`}
                >
                  {t.plans.select}
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Memberships;
